/**
 * Platform Renderer (Simplified)
 *
 * Phase 4.3.3 Revision: All platforms are rounded rectangles
 * - Reliable physics (shape matches visual exactly)
 * - Color-coded by surface type for gameplay clarity
 * - Subtle texture variations for visual interest
 * - Zone theming through decorations only
 */

import Phaser from 'phaser';
import { SurfaceType, GAME_CONSTANTS } from '../config/constants';
import { getSurfaceTypeColor, getSurfaceTypeDarkColor } from '../level/ZoneThemes';
import { SeededRandom, generatePlatformSeed } from '../utils/SeededRandom';
import { DecoratorFactory } from './decorators/DecoratorFactory';
import { PlatformDecoratorBase } from './decorators/PlatformDecoratorBase';

export class PlatformRenderer {
  private graphics: Phaser.GameObjects.Graphics;
  private surfaceType: SurfaceType;
  private random: SeededRandom;
  private decorator: PlatformDecoratorBase;
  private colorVariation: number;

  constructor(scene: Phaser.Scene, zoneId: number, surfaceType: SurfaceType, platformId: number) {
    this.surfaceType = surfaceType;

    // Create deterministic random generator for this platform
    const seed = generatePlatformSeed(platformId, zoneId);
    this.random = new SeededRandom(seed);

    // Select color variation (0-2 for 3 color options per surface type)
    this.colorVariation = this.random.nextInt(0, 3);

    // Create graphics object
    this.graphics = scene.add.graphics();

    // Create decorator for this platform using factory
    this.decorator = DecoratorFactory.createDecorator(scene, zoneId, surfaceType, this.random);
  }

  /**
   * Render the platform at given position
   * @param container - Container to add decorations to (optional)
   */
  render(
    x: number,
    y: number,
    width: number,
    height: number,
    container?: Phaser.GameObjects.Container
  ): Phaser.GameObjects.Graphics {
    this.graphics.setPosition(x, y);
    this.graphics.clear();

    // All platforms are rounded rectangles
    this.drawRoundedRectangle(width, height);

    // Add decorations if container is provided (iOS Performance: Can be disabled for testing)
    if (container && !GAME_CONSTANTS.DEBUG.DISABLE_DECORATIONS) {
      this.decorator.decorate(container, 0, 0, width, height);
    }

    return this.graphics;
  }

  /**
   * Draw rounded rectangle with surface-type-specific textures
   */
  private drawRoundedRectangle(width: number, height: number): void {
    const cornerRadius = 8;
    const baseColor = getSurfaceTypeColor(this.surfaceType, this.colorVariation);
    const darkColor = getSurfaceTypeDarkColor(this.surfaceType);

    // Draw base rounded rectangle
    this.graphics.fillStyle(baseColor, 1);
    this.graphics.fillRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);

    // Add surface-type-specific texture
    switch (this.surfaceType) {
      case SurfaceType.STICKY:
        this.drawStickyTexture(width, height, darkColor);
        break;
      case SurfaceType.SLIPPERY:
        this.drawSlipperyTexture(width, height);
        break;
      case SurfaceType.BOUNCY:
        this.drawBouncyTexture(width, height);
        break;
      case SurfaceType.ANGLED:
        this.drawAngledTexture(width, height, darkColor);
        break;
      case SurfaceType.NORMAL:
      default:
        this.drawNormalTexture(width, height, darkColor);
        break;
    }

    // Draw outline
    this.graphics.lineStyle(2, 0x000000, 0.3);
    this.graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, cornerRadius);
  }

  /**
   * NORMAL texture: Wood grain lines
   */
  private drawNormalTexture(width: number, height: number, darkColor: number): void {
    this.graphics.lineStyle(1, darkColor, 0.3);

    // Draw 2-4 horizontal wood grain lines
    const numLines = this.random.nextInt(2, 5);
    for (let i = 0; i < numLines; i++) {
      const y = this.random.nextFloat(-height * 0.3, height * 0.3);
      const startX = -width * 0.4 + this.random.nextFloat(-5, 5);
      const endX = width * 0.4 + this.random.nextFloat(-5, 5);
      this.graphics.lineBetween(startX, y, endX, y);
    }
  }

  /**
   * STICKY texture: Moss/grass dots
   */
  private drawStickyTexture(width: number, height: number, darkColor: number): void {
    // Draw scattered moss dots
    const numDots = this.random.nextInt(8, 15);
    this.graphics.fillStyle(darkColor, 0.4);

    for (let i = 0; i < numDots; i++) {
      const dotX = this.random.nextFloat(-width * 0.4, width * 0.4);
      const dotY = this.random.nextFloat(-height * 0.3, height * 0.3);
      const dotSize = this.random.nextFloat(1, 2.5);
      this.graphics.fillCircle(dotX, dotY, dotSize);
    }
  }

  /**
   * SLIPPERY texture: Ice shine/reflection lines
   */
  private drawSlipperyTexture(width: number, height: number): void {
    // Draw diagonal shine lines
    this.graphics.lineStyle(1.5, 0xffffff, 0.4);

    const numShines = this.random.nextInt(2, 4);
    for (let i = 0; i < numShines; i++) {
      const startX = this.random.nextFloat(-width * 0.3, width * 0.1);
      const startY = -height * 0.4;
      const endX = startX + width * 0.2;
      const endY = -height * 0.4 + height * 0.3;
      this.graphics.lineBetween(startX, startY, endX, endY);
    }

    // Add a few sparkle dots
    this.graphics.fillStyle(0xffffff, 0.6);
    for (let i = 0; i < 3; i++) {
      const sparkleX = this.random.nextFloat(-width * 0.3, width * 0.3);
      const sparkleY = this.random.nextFloat(-height * 0.2, height * 0.2);
      this.graphics.fillCircle(sparkleX, sparkleY, 1);
    }
  }

  /**
   * BOUNCY texture: Spring coil pattern
   */
  private drawBouncyTexture(width: number, height: number): void {
    // Draw small coil/wave pattern
    this.graphics.lineStyle(1.5, 0xffffff, 0.5);

    const waveY = 0;
    const numWaves = 3;
    const waveWidth = (width * 0.6) / numWaves;

    this.graphics.beginPath();
    this.graphics.moveTo(-width * 0.3, waveY);

    for (let i = 0; i < numWaves; i++) {
      const x = -width * 0.3 + i * waveWidth;
      this.graphics.lineTo(x + waveWidth / 2, waveY - 3);
      this.graphics.lineTo(x + waveWidth, waveY);
    }
    this.graphics.strokePath();

    // Add energy dots
    this.graphics.fillStyle(0xffff00, 0.6);
    for (let i = 0; i < 4; i++) {
      const dotX = this.random.nextFloat(-width * 0.3, width * 0.3);
      const dotY = this.random.nextFloat(-height * 0.2, height * 0.2);
      this.graphics.fillCircle(dotX, dotY, 1.5);
    }
  }

  /**
   * ANGLED texture: Stone/rock cracks
   */
  private drawAngledTexture(width: number, height: number, darkColor: number): void {
    // Draw crack lines
    this.graphics.lineStyle(1, darkColor, 0.4);

    const numCracks = this.random.nextInt(2, 4);
    for (let i = 0; i < numCracks; i++) {
      const startX = this.random.nextFloat(-width * 0.3, width * 0.3);
      const startY = this.random.nextFloat(-height * 0.3, height * 0.3);
      const length = this.random.nextFloat(10, 20);
      const angle = this.random.nextFloat(0, Math.PI * 2);
      const endX = startX + Math.cos(angle) * length;
      const endY = startY + Math.sin(angle) * length;
      this.graphics.lineBetween(startX, startY, endX, endY);
    }
  }

  /**
   * Get the graphics object
   */
  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  /**
   * Get the decorator for animation updates
   */
  getDecorator(): PlatformDecoratorBase {
    return this.decorator;
  }

  /**
   * Update animations (call from platform update)
   */
  updateAnimations(deltaTime: number): void {
    this.decorator.updateAnimations(deltaTime);
  }

  /**
   * Destroy and clean up
   */
  destroy(): void {
    this.decorator.destroy();
    this.graphics.destroy();
  }
}
